import { useEffect, useMemo } from "react";
import { debounce } from "lodash";
import UseQuery from "@/shared/hooks/use-query.hook";
import useModal from "@rf/base/modal/useModal";
import { useSedFeedbackMutation } from "@/src-refactor/modules/store/qrtk/services/realestate/realestate.qrtk";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  IAssessmentForm,
  assessmentInitialForm,
  ASSESSMENT_FORM_ENUMS
} from "../utils/feedback/form.util";
import { assessmentAdapter } from "../utils/feedback/assessment-adapter";
import { validationAssessmentSchema } from "../utils/feedback/validation-schema";
import MxFormL3TextAreaField from "@rf-l3/form/MxFormL3TextAreaField";
import MxFormL3RatingField from "@rf-l3/form/MxFormL3RatingField";
import MxTypography from "@rf-l1/MxTypography";
import BtnSimple from "@/src-refactor/modules/shared/components/buttons/BtbSimple";
import styled from "@emotion/styled";
import toast from "react-hot-toast";
import { usePropertyDetailPage } from "../context/PropertyDetailPageContext";
import { useLocation, useNavigate } from "react-router-dom";
import MxModalTemplate2 from "@rf-l1/modal/MxModalTemplate2";
import useReadOnlyModeGuard from "@rf-hooks/useReadOnlyModeGuard";

const usePropertyFeedback = () => {
  const { getQuery } = UseQuery();
  const { modal } = useModal();
  const propertyDetail = usePropertyDetailPage();
  const [sendFeedback, { isLoading }] = useSedFeedbackMutation();
  const { guardReadOnlyAction } = useReadOnlyModeGuard();

  const isProject = useMemo(
    () => propertyDetail?.isProject || false,
    [propertyDetail]
  );

  const showFeedbackModal = () => {
    guardReadOnlyAction("enviar valoración", "property-feedback");
  };

  const loadReview = debounce(() => {
    showFeedbackModal();
  }, 500);

  useEffect(() => {
    const { isNewProperty } = getQuery();
    if (isNewProperty) {
      loadReview();
    }
    return () => {
      loadReview.cancel();
    };
  }, []);

  return { showFeedbackModal, isLoading };
};

export default usePropertyFeedback;

interface FeedbackModalContentProps {
  isProject: boolean;
}

const FeedbackModalContent: React.FC<FeedbackModalContentProps> = ({
  isProject
}) => {
  const [sendFeedback, { isLoading }] = useSedFeedbackMutation();
  const { onConfirm } = useModal();
  const navigate = useNavigate();
  const location = useLocation();
  const { guardReadOnlyAction } = useReadOnlyModeGuard();

  const methods = useForm<IAssessmentForm>({
    defaultValues: assessmentInitialForm,
    resolver: yupResolver(validationAssessmentSchema)
  });

  const { handleSubmit, control } = methods;

  const onSubmit = (values: IAssessmentForm) => {
    guardReadOnlyAction("enviar valoración", "property-feedback");
  };

  return (
    <MxModalTemplate2>
      <FormProvider {...methods}>
        <Container onSubmit={handleSubmit(onSubmit)}>
          <MxTypography color="grey.600" variant="body2">
            Apreciamos enormemente tu opinión, ya que es fundamental para nuestra
            mejora continua. ¿Qué tan cómodo/a te sentiste al utilizar nuestro nuevo
            módulo? Tu experiencia es clave para perfeccionar nuestro servicio, y
            agradecemos cualquier comentario que puedas compartir.
          </MxTypography>

          <ContainerForms>
            <MxFormL3RatingField
              name={ASSESSMENT_FORM_ENUMS.rating}
              size="large"
              withreacthookform={1}
            />

            <MxFormL3TextAreaField
              label="Comentario"
              withreacthookform={1}
              name={ASSESSMENT_FORM_ENUMS.comment}
              placeholder="Comentario"
            />
          </ContainerForms>

          <ContainerButtons>
            <BtnSimple type="submit" isLoading={isLoading}>
              Enviar
            </BtnSimple>
          </ContainerButtons>
        </Container>
      </FormProvider>
    </MxModalTemplate2>
  );
};
// --- Styles ---

const Container = styled.form`
  width: 100%;
  .letter-content {
    text-align: justify;
    text-justify: inter-word;
  }
`;

const ContainerForms = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  justify-items: center;
  margin-top: 20px;
  gap: 20px;
  span {
    align-self: center;
  }
`;

const ContainerButtons = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
`;
